import { Controller, Control, FieldErrors } from 'react-hook-form';
import { Label } from '@/shared/components/ui/label';
import type { ProviderEditorFormData } from '../schemas';
import { useAvailabilitySchedules } from '../../../hooks/useReferenceData';
import { cn } from '@/lib/utils';

type AvailabilitySectionProps = {
  control: Control<ProviderEditorFormData>;
  errors: FieldErrors<ProviderEditorFormData>;
};

/**
 * AvailabilitySection - Provider availability and scheduling
 * 
 * Single-select availability schedule (one option only)
 * Stored in provider_availability_schedules junction table
 * 
 * Examples: "Weekdays Only", "24/7 Available", "Weekends Only", "By Appointment"
 * 
 * Uses native select dropdown for single selection
 * Fetches reference data from backend reference-data API endpoint
 */
export function AvailabilitySection({
  control,
  errors,
}: AvailabilitySectionProps) {
  // Fetch availability schedule reference data
  const { data: availabilitySchedules = [], isLoading } = useAvailabilitySchedules();

  return (
    <div className="space-y-6">
      {/* Availability Schedule (Single Select) */}
      <div>
        <Label htmlFor="availabilityScheduleId" className="text-sm font-medium">
          Availability Schedule
        </Label>
        <p className="text-sm text-gray-500 mt-1 mb-2">
          Select when your services are available (e.g., Weekdays Only, 24/7, Weekends, By Appointment)
        </p>
        <Controller
          name="availabilityScheduleId"
          control={control}
          render={({ field }) => (
            <div className="relative">
              <select
                id="availabilityScheduleId"
                value={field.value || ''}
                onChange={field.onChange}
                disabled={isLoading}
                className={cn(
                  'flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm',
                  'ring-offset-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                  errors.availabilityScheduleId && 'border-red-500 focus:ring-red-500'
                )}
              >
                <option value="">Select availability schedule...</option>
                {availabilitySchedules.map((schedule) => (
                  <option key={schedule.id} value={schedule.id}>
                    {schedule.name}
                  </option>
                ))}
              </select>
              {errors.availabilityScheduleId && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.availabilityScheduleId.message}
                </p>
              )}
            </div>
          )}
        />
      </div>

      {/* Additional Context */}
      <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
        <p className="text-sm text-blue-800">
          <strong>Tip:</strong> You can also use the "Hours Description" field in the Contact Details section to provide more detailed availability information (e.g., "Monday-Friday 9am-5pm EST").
        </p>
      </div>
    </div>
  );
}

